// X72 (Grant 2026-10-20, the admin calendar): "admins should be able to do
// what they want, not having to adhere to the normal rules", and "the dialog
// should offer commonly used lengths and then an other for an admin". The
// pure half: the Book dialog's Length choices for an admin (the common
// lengths that fit the day and stop before a clash, "Rest of the day", and
// "Other…" for any end time), and the words for a change outside the rules.
// The API is the authority: it chips every rule for an admin and refuses only
// a clash (utils/bookingDrag's validity mirrors that on the grid).

import {TAKEN, durationWords} from './bookingDrag.js';
import {clockInZone, instantInZone, dayInZone, gridTime} from './bookings.js';

export const LENGTH_OTHER = 'other';
export const LENGTH_REST = 'rest';

/** The common lengths, in minutes, that sit on the group's step (40-minute slots: 40, 80, 120…; 30: 30, 60, 90…; 60: 60, 120…). */
export function commonLengths(step) {
  const s = Math.max(5, step || 60);
  const out = new Set();
  // A step the round numbers don't sit on (40, 45, 50…) offers its own multiples instead, up to four hours.
  if (30 % s !== 0 && s % 30 !== 0) for (let m = s; m <= 240; m += s) out.add(m);
  else for (const m of [s, 30, 60, 90, 120, 180, 240]) if (m >= s && m % s === 0) out.add(m);
  return [...out].sort((a, b) => a - b);
}

/**
 * Minutes from a start to the end of the group's last slot that day on an
 * area, stopping at the first slot taken by a booking (a clash is the one hard
 * stop; closed, frozen, matches-only and walk-up time are chips the admin may
 * take). Null when the start is not among the slots. X82 (R29 L1): with the
 * group's `step`, a run shorter than one step is null too: "Rest of the day"
 * is never shorter than the smallest length pill (the API keeps its made-up
 * open-play slots on the step now, but a slot list is never trusted for it).
 */
export function restOfDayMinutes(slots, areaId, startsAt, step = null) {
  const list = (slots ?? []).slice().sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
  const t = startsAt ? new Date(startsAt).getTime() : NaN;
  const i = list.findIndex(s => new Date(s.startsAt).getTime() === t);
  if (i < 0) return null;
  let end = t;
  for (let j = i; j < list.length; j++) {
    const s = list[j];
    if (new Date(s.startsAt).getTime() !== end) break; // a gap in the hours
    const cell = (s.areas ?? []).find(c => c.areaId === areaId);
    if (cell && (cell.bookingId || cell.status === 'booked' || cell.status === 'mine' || cell.status === 'match')) break;
    end = new Date(s.endsAt).getTime();
  }
  const minutes = Math.round((end - t) / 60000);
  if (minutes <= 0) return null;
  if (step && minutes < Math.max(5, step)) return null;
  return minutes;
}

/**
 * The Length select for an admin: the common lengths that fit before the day's
 * end or the next booking, the one the drag asked for if it is not among them,
 * "Rest of the day" and "Other…". `minutes` chosen stays even when it no longer
 * fits (the API chips it).
 */
export function lengthChoices({step, restMinutes = null, askedMinutes = null, chosen = null}) {
  const s = Math.max(5, step || 60);
  const items = [];
  const fits = (m) => restMinutes == null || m <= restMinutes;
  const set = new Set(commonLengths(s).filter(fits));
  if (askedMinutes && askedMinutes > 0) set.add(askedMinutes);
  if (typeof chosen === 'number' && chosen > 0) set.add(chosen);
  for (const m of [...set].sort((a, b) => a - b)) items.push({value: m, title: lengthTitle(m, s)});
  // X82: a rest shorter than the step is no "Rest of the day" (no pill ever reads a length under the step; the asked step stays).
  const rest = restMinutes != null && restMinutes >= s ? restMinutes : null;
  if (rest && !set.has(rest)) items.push({value: LENGTH_REST, title: `Rest of the day (${durationWords(rest)})`});
  else if (rest) items.push({value: LENGTH_REST, title: 'Rest of the day'});
  items.push({value: LENGTH_OTHER, title: 'Other…'});
  return items;
}

/** "1 h (1 slot)", "1 h 30 (3 slots)", "2 h (3 slots)" on a 40-minute step. */
export function lengthTitle(minutes, step) {
  const s = Math.max(5, step || 60);
  const slots = Math.round(minutes / s);
  const whole = minutes % s === 0;
  return whole ? `${durationWords(minutes)} (${slots} slot${slots === 1 ? '' : 's'})` : durationWords(minutes);
}

/** The length the drag or a click asked for, in minutes (X40's initialSlots on the group's step). */
export function askedMinutes(initialSlots, step) {
  const n = Math.max(1, Math.round(initialSlots || 1));
  return n * Math.max(5, step || 60);
}

/**
 * The end instant of a custom "HH:mm" on the start's club day (a time at or
 * before the start is the next day only when it is "00:00" / "24:00").
 */
export function customEndInstant(startsAt, hhmm, timeZone) {
  if (!startsAt || !hhmm) return null;
  const date = dayInZone(startsAt, timeZone);
  if (hhmm === '24:00' || hhmm === '00:00') {
    const [y, m, d] = date.split('-').map(n => parseInt(n, 10));
    const next = new Date(Date.UTC(y, m - 1, d + 1));
    return instantInZone(`${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(next.getUTCDate()).padStart(2, '0')}`, '00:00', timeZone);
  }
  return instantInZone(date, hhmm, timeZone);
}

/** The "HH:mm" an end instant reads as on the club's clock, for the Other… box's starting value. */
export function endClock(startsAt, minutes, timeZone) {
  if (!startsAt) return '';
  const end = new Date(new Date(startsAt).getTime() + (minutes || 0) * 60000).toISOString();
  const clock = clockInZone(end, timeZone);
  return clock === '00:00' ? '24:00' : clock;
}

/**
 * What the body sends for a chosen length: whole slots (`slots`) when the
 * length sits on the step, else the exact end (`endsAt`, the admin's "Other…"
 * or "Rest of the day" that ends off the step, X72's API field).
 */
export function lengthBody({choice, step, startsAt, restMinutes, customHhmm, timeZone}) {
  const s = Math.max(5, step || 60);
  if (choice === LENGTH_OTHER) {
    const endsAt = customEndInstant(startsAt, customHhmm, timeZone);
    if (!endsAt) return {slots: 1};
    const minutes = Math.round((new Date(endsAt).getTime() - new Date(startsAt).getTime()) / 60000);
    if (minutes <= 0) return {slots: 1, invalid: 'The end has to be after the start.'};
    return minutes % s === 0 ? {slots: minutes / s} : {slots: Math.ceil(minutes / s), endsAt};
  }
  const minutes = choice === LENGTH_REST ? restMinutes : choice;
  if (!minutes || minutes <= 0) return {slots: 1};
  if (minutes % s === 0) return {slots: minutes / s};
  const endsAt = new Date(new Date(startsAt).getTime() + minutes * 60000).toISOString();
  return {slots: Math.ceil(minutes / s), endsAt};
}

/**
 * X76 (R26 M1): the Book dialog's Start choices for an admin: EVERY slot of the day on the area, the past ones too, each
 * with the chip the API would put on it ("10am · closed", "9:30am · frozen", "6pm · matches only", "8am · past"); only a
 * slot holding a booking is disabled ("taken", the one hard stop). Open-play changes have no slots, so their time is
 * offered on the group's step ("1:20pm · open play"); `extra` (the instant a drag or a click on the grid asked for) is
 * added as "not a slot start" when no slot names it. Sorted by time; each item carries `time` and `past`.
 */
export function adminStartOptions({slots, openChanges = [], areaId, step, now = Date.now(), timeZone = null, extra = null}) {
  const items = [];
  const has = (t) => items.some(i => i.time === t);
  const push = (t, chips, disabled = false) => {
    const past = t <= now;
    const words = [...chips, ...(past ? ['past'] : [])].map(c => ` · ${c}`).join('');
    items.push({value: new Date(t).toISOString(), time: t, title: gridTime(new Date(t).toISOString(), timeZone) + words, props: {disabled}, past});
  };
  for (const s of slots ?? []) {
    const t = new Date(s.startsAt).getTime();
    if (Number.isNaN(t) || has(t)) continue;
    const cell = (s.areas ?? []).find(c => c.areaId === areaId);
    const taken = !!(cell && (cell.bookingId || TAKEN.has(cell.status)));
    // X78 (R27 N1): a made-up slot (s.openPlay, a booking's time in open play) reads "open play" on the other areas, not "walk-up".
    const chip = taken ? 'taken' : cell?.status === 'frozen' ? 'frozen' : cell?.status === 'closed' ? 'closed' : cell?.status === 'matchesOnly' ? 'matches only' : s.openPlay ? 'open play' : cell?.status === 'open' ? 'walk-up' : null;
    // the API's own slot string, so a select built from it matches what the calendar meant
    const past = t <= now;
    items.push({value: s.startsAt, time: t, title: gridTime(s.startsAt, timeZone) + (chip ? ` · ${chip}` : '') + (past ? ' · past' : ''), props: {disabled: taken}, past});
  }
  const ms = Math.max(5, step || 60) * 60000;
  for (const o of openChanges ?? []) {
    const from = new Date(o.startsAt).getTime();
    const to = new Date(o.endsAt).getTime();
    if (Number.isNaN(from) || Number.isNaN(to)) continue;
    for (let t = from; t + ms <= to; t += ms) if (!has(t)) push(t, ['open play']);
  }
  if (extra) {
    const t = new Date(extra).getTime();
    if (!Number.isNaN(t) && !has(t)) push(t, ['not a slot start']);
  }
  return items.sort((a, b) => a.time - b.time);
}

/** The start the dialog opens on when nothing was asked for: the first that can be picked and is still to come, else the first that can be picked. */
export function defaultStart(options) {
  return options.find(o => !o.props?.disabled && !o.past)?.value ?? options.find(o => !o.props?.disabled)?.value ?? null;
}

/** The admin's chips, worded for the ghost and the confirm dialog ("outside hours" → "Outside hours"). */
export function chipWords(chips) {
  return (chips ?? []).map(c => c.charAt(0).toUpperCase() + c.slice(1));
}

/** "This booking is outside the group's hours." lines for the confirm dialog, one per chip the API or the grid found. */
export function overrideLines(chips) {
  const words = {
    'outside hours': 'This booking is outside the booking hours.',
    'open play': 'This time is open play: nothing is normally booked then.',
    'closed time': 'This time is closed.',
    'matches only': 'This time is kept for matches.',
    'in the past': 'This time has already gone.',
    'longer than allowed': 'It is longer than the booker is allowed.',
    'beyond the window': "It is further ahead than the booker's window.",
    'more than allowed at once': 'The booker already has as many bookings as allowed.',
    'not a slot start': 'It does not start on a slot.',
    'not a slot end': 'It does not end on a slot.',
    'frozen': 'The playing area is frozen then.',
    'walk-up area': 'The playing area is a walk-up area.',
    'not allowed to book': 'The booker is not allowed to book here.',
    // X81 (X80's note): BookingService.Prepare's own two chips, worded as the app words them.
    'no charge': 'The member is not charged for it.',
    'guest not allowed': 'Guests are not allowed on bookings here.',
  };
  return (chips ?? []).map(c => words[c] ?? words[c.toLowerCase()] ?? `Outside the rules: ${c}.`);
}
